import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaUserFriends, 
  FaSpinner, 
  FaCheck, 
  FaTimes, 
  FaClock, 
  FaMoneyBillWave,
  FaInbox,
  FaPaperPlane
} from 'react-icons/fa';
import { friendPaymentApi } from '../utils/friendPaymentApi';
import { showSuccessNotification, showErrorNotification } from './NotificationManager';

const PaymentRequestsList = () => {
  const [requests, setRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('received');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  const fetchPaymentRequests = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all requests regardless of status to show complete history
      const response = await friendPaymentApi.getPaymentRequests(activeTab);
      if (response.success) {
        setRequests(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch payment requests:', error);
      showErrorNotification('Failed to load payment requests');
    } finally {
      setLoading(false);
    }
  }, [activeTab]); 

  useEffect(() => {
    fetchPaymentRequests();
  }, [fetchPaymentRequests]); 

  const handleAcceptRequest = async (requestId) => {
    setActionLoading(prev => ({ ...prev, [requestId]: 'accepting' }));
    try {
      const response = await friendPaymentApi.acceptPaymentRequest(requestId, 'wallet');
      if (response.success) {
        showSuccessNotification('Payment request accepted! Your friend will be notified.');
        fetchPaymentRequests();
      } else {
        showErrorNotification(response.message || 'Failed to accept payment request');
      }
    } catch (error) {
      showErrorNotification('Failed to accept payment request');
    } finally {
      setActionLoading(prev => ({ ...prev, [requestId]: null }));
    }
  };

  const handleDeclineRequest = async (requestId, reason) => {
    setActionLoading(prev => ({ ...prev, [requestId]: 'declining' }));
    try {
      const response = await friendPaymentApi.declinePaymentRequest(requestId, reason);
      if (response.success) {
        showSuccessNotification('Payment request declined.');
        fetchPaymentRequests();
      } else {
        showErrorNotification(response.message || 'Failed to decline payment request');
      }
    } catch (error) {
      showErrorNotification('Failed to decline payment request');
    } finally {
      setActionLoading(prev => ({ ...prev, [requestId]: null }));
    }
  };

  const handleCancelRequest = async (requestId) => {
    setActionLoading(prev => ({ ...prev, [requestId]: 'cancelling' }));
    try {
      const response = await friendPaymentApi.cancelPaymentRequest(requestId);
      if (response.success) {
        showSuccessNotification('Payment request cancelled.');
        fetchPaymentRequests();
      } else {
        showErrorNotification(response.message || 'Failed to cancel payment request');
      }
    } catch (error) {
      showErrorNotification('Failed to cancel payment request');
    } finally {
      setActionLoading(prev => ({ ...prev, [requestId]: null }));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  

  const isExpired = (expiresAt) => new Date(expiresAt) < new Date();

  const getStatusInfo = (request) => {
    const status = request.status?.toLowerCase();
    const expired = isExpired(request.expiresAt);
    
    if (expired && status === 'pending') {
      return {
        label: 'EXPIRED',
        color: 'text-red-500',
        bgColor: 'bg-red-50 border-red-200',
        showActions: false
      };
    }
    
    switch (status) {
      case 'accepted':
        return {
          label: 'PAID',
          color: 'text-green-600',
          bgColor: 'bg-green-50 border-green-200',
          showActions: false,
          icon: 'FaCheck',
          details: request.paymentMethod ? `via ${request.paymentMethod}` : '',
          date: request.respondedAt
        };
      case 'rejected':
      case 'declined':
        return {
          label: 'DECLINED',
          color: 'text-red-600',
          bgColor: 'bg-red-50 border-red-200',
          showActions: false,
          icon: 'FaTimes',
          date: request.respondedAt
        };
      case 'cancelled':
        return {
          label: 'CANCELLED',
          color: 'text-gray-600',
          bgColor: 'bg-gray-50 border-gray-200',
          showActions: false,
          icon: 'FaTimes',
          date: request.respondedAt
        };
      case 'pending':
      default:
        return {
          label: 'PENDING',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50 border-yellow-200',
          showActions: true,
          icon: 'FaClock'
        };
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <h1 className="text-3xl font-bold text-crust font-display flex items-center justify-center gap-3">
          <FaUserFriends className="text-pepperoni" />
          Payment Requests
        </h1>
        <p className="text-olive-light font-sans mt-2">Manage your friend payment requests</p>
      </motion.div>

      <div className="bg-accentwhite rounded-2xl shadow-card border border-secondary-200 overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b border-secondary-200">
          {['received', 'sent'].map((tab) => (
            <motion.button
              key={tab}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-4 px-6 font-bold font-display transition-all duration-300 ${
                activeTab === tab
                  ? 'bg-gradient-to-r from-cheese to-secondary-500 text-crust border-b-2 border-pepperoni'
                  : 'text-olive-light hover:text-crust hover:bg-secondary-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                {tab === 'received' ? <FaInbox /> : <FaPaperPlane />}
                <span>{tab === 'received' ? 'Received' : 'Sent'}</span>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <FaSpinner className="animate-spin text-2xl text-pepperoni mr-3" />
              <span className="text-olive-light font-sans">Loading requests...</span>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                {requests.length === 0 ? (
                  <div className="text-center py-12">
                    <FaUserFriends className="text-4xl text-secondary-300 mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-crust font-display mb-2">
                      No {activeTab} payment requests
                    </h3>
                    <p className="text-olive-light font-sans">
                      {activeTab === 'received'
                        ? "You don't have any payment requests from friends."
                        : "You haven't sent any payment requests to friends."}
                    </p>
                  </div>
                ) : (
                  requests.map((request) => {
                    const statusInfo = getStatusInfo(request);
                    return (
                    <motion.div
                      key={request._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`rounded-xl p-4 sm:p-6 border-2 flex flex-col gap-4 transition-all duration-300 ${
                        statusInfo.bgColor
                      } hover:shadow-md`}
                    >
                      {/* Details */}
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-3 mb-4">
                            <div className="bg-pepperoni rounded-full p-2 flex-shrink-0">
                              <FaMoneyBillWave className="text-accentwhite text-sm" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                                <h4 className="font-bold text-crust font-display break-words text-base sm:text-lg">
                                  {activeTab === 'received'
                                    ? `@${request.requester.username} wants you to pay ₦${request.amount.toLocaleString()}`
                                    : `Waiting for @${request.payer.username} to pay ₦${request.amount.toLocaleString()}`}
                                </h4>
                                <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold ${statusInfo.color} bg-opacity-20`}>
                                  {statusInfo.icon === 'FaCheck' && <FaCheck />}
                                  {statusInfo.icon === 'FaTimes' && <FaTimes />}
                                  {statusInfo.icon === 'FaClock' && <FaClock />}
                                  <span>{statusInfo.label}</span>
                                  {statusInfo.details && <span className="text-xs opacity-75">({statusInfo.details})</span>}
                                </div>
                              </div>
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-olive-light font-sans">
                                <span>Order: <span className="font-semibold">{request.orderId?.customId || 'N/A'}</span></span>
                                {statusInfo.date && (
                                  <span className="text-xs">• Completed: {formatDate(statusInfo.date)}</span>
                                )}
                              </div>
                            </div>
                          </div>

                          {request.message && (
                            <div className="bg-accentwhite rounded-lg p-3 sm:p-4 border border-secondary-200 mb-4">
                              <p className="text-sm text-crust font-sans italic break-words">
                                "{request.message}"
                              </p>
                            </div>
                          )}

                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs text-olive-light font-sans">
                            <div className="flex items-center gap-1">
                              <FaClock />
                              <span>Created: {formatDate(request.createdAt)}</span>
                            </div>
                            {request.status === 'pending' && (
                              <div className="flex items-center gap-1">
                                <span>Expires: {formatDate(request.expiresAt)}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        {statusInfo.showActions && (
                          <div className="flex flex-col sm:flex-row gap-3 lg:flex-col lg:items-end w-full sm:w-auto">
                            {activeTab === 'received' ? (
                              <>
                                <motion.button
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  onClick={() => handleAcceptRequest(request._id)}
                                  disabled={actionLoading[request._id]}
                                  className="bg-gradient-to-r from-cheese to-secondary-500 hover:from-secondary-500 hover:to-cheese text-crust px-4 py-3 rounded-lg font-bold font-display text-sm shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 w-full sm:w-auto lg:w-32"
                                >
                                  {actionLoading[request._id] === 'accepting' ? (
                                    <FaSpinner className="animate-spin mx-auto" />
                                  ) : (
                                    <div className="flex items-center justify-center gap-2">
                                      <FaCheck />
                                      <span>Pay</span>
                                    </div>
                                  )}
                                </motion.button>
                                <motion.button
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  onClick={() => {
                                    const reason = prompt('Reason for declining (optional):');
                                    if (reason !== null) {
                                      handleDeclineRequest(request._id, reason);
                                    }
                                  }}
                                  disabled={actionLoading[request._id]}
                                  className="bg-gradient-to-r from-primary-600 to-pepperoni hover:from-pepperoni hover:to-primary-600 text-accentwhite px-4 py-3 rounded-lg font-bold font-display text-sm shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 w-full sm:w-auto lg:w-32"
                                >
                                  {actionLoading[request._id] === 'declining' ? (
                                    <FaSpinner className="animate-spin mx-auto" />
                                  ) : (
                                    <div className="flex items-center justify-center gap-2">
                                      <FaTimes />
                                      <span>Decline</span>
                                    </div>
                                  )}
                                </motion.button>
                              </>
                            ) : (
                              <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => handleCancelRequest(request._id)}
                                disabled={actionLoading[request._id]}
                                className="bg-gradient-to-r from-primary-600 to-pepperoni hover:from-pepperoni hover:to-primary-600 text-accentwhite px-4 py-3 rounded-lg font-bold font-display text-sm shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 w-full sm:w-auto lg:w-32"
                              >
                                {actionLoading[request._id] === 'cancelling' ? (
                                  <FaSpinner className="animate-spin mx-auto" />
                                ) : (
                                  <div className="flex items-center justify-center gap-2">
                                    <FaTimes />
                                    <span>Cancel</span>
                                  </div>
                                )}
                              </motion.button>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                    );
                  })
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentRequestsList;
